/**
 * Routing matrix: one reusable table that says which departments review a
 * request and who reviews for each department at each site.
 *
 *   departments   key, label, functional role, wave (1 = first), an optional
 *                 override field (a people field on the form whose value wins
 *                 over the matrix), whether it reviews all requests, and the
 *                 questions its reviewer answers
 *   rules         when the form says X (site, change type, class, an impact
 *                 answer, ...), these departments review
 *   assignments   role + site -> a person or a People Group (@Name); a row with
 *                 no site is the default for all sites
 *
 * Stored in wff_routing_matrices (migration 00533) and edited on Setup >
 * Routing Matrix. A "Matrix review" step expands it when the step opens:
 * each selected department becomes a review seat in its wave, the assignee is
 * resolved override field first, then the site row, then the default row.
 * A "Department review" step uses the same matrix for one named department.
 *
 * Pure: no I/O. Persistence lives in supabaseService (getRoutingMatrices...).
 */
import type { PeopleGroup } from '@/types/people-group'
import type { PlanWave, ReviewQuestion, ReviewSeat, WaveJoinRule } from '@/types/workflows-v3'
import { expandTokens } from './groups'

export type MatrixConditionOp = 'eq' | 'neq' | 'in' | 'contains' | 'not_empty'

export interface MatrixCondition {
  /** Form field key (without the form. prefix). */
  field: string
  op: MatrixConditionOp
  /** One value, or several for `in`. Ignored for not_empty. */
  value: string | string[]
}

export interface MatrixRule {
  id: string
  label?: string
  /** all = every condition holds; any = at least one does. */
  match: 'all' | 'any'
  conditions: MatrixCondition[]
  /** Department keys that review when the rule holds. */
  departments: string[]
}

export interface MatrixDepartment {
  key: string
  label: string
  /** Functional role the department's reviewer holds (assignments key on it). */
  role: string
  /** 1-based review wave. Wave 2 opens after wave 1 completes. */
  wave: number
  /** Reviews all requests, whatever the rules say. */
  always?: boolean
  /** A people field on the form whose value names the reviewer for this
   *  department. The originator or a checker fills it; it wins over the matrix. */
  overrideField?: string
  questions?: ReviewQuestion[]
}

export interface MatrixAssignment {
  role: string
  /** Site id (the site field's value). Blank = default for all sites. */
  site: string
  /** A person (display name) or @People Group. */
  assignee: string
}

export interface RoutingMatrix {
  id: string
  key: string
  name: string
  description: string | null
  active: boolean
  /** Form field that carries the request's site. Default site_id. */
  siteField: string
  departments: MatrixDepartment[]
  rules: MatrixRule[]
  assignments: MatrixAssignment[]
  createdAt?: string
  updatedAt?: string
  updatedBy?: string | null
}

export const DEFAULT_SITE_FIELD = 'site_id'
export const ROUTING_MATRIX_PATH = '/workflow-management/routing-matrix'

// ---------------------------------------------------------------------------
// Normalization + validation (mirrors the 00533 trigger)
// ---------------------------------------------------------------------------

const KEY_RE = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim()
}

export function slugKey(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48)
}

function normQuestion(raw: unknown, i: number): ReviewQuestion | null {
  const q = (raw ?? {}) as Record<string, unknown>
  const label = str(q.label)
  if (!label) return null
  const type = (['text', 'yes_no', 'select', 'number'] as const).includes(q.type as never) ? q.type as ReviewQuestion['type'] : 'text'
  const options = Array.isArray(q.options) ? q.options.map(str).filter(Boolean) : undefined
  return { id: str(q.id) || `q${i + 1}`, label, type, ...(type === 'select' && options?.length ? { options } : {}), ...(q.required ? { required: true } : {}) }
}

export function normalizeMatrix(row: Partial<RoutingMatrix> & Record<string, unknown>): RoutingMatrix {
  const departments = (Array.isArray(row.departments) ? row.departments : []).map((d) => {
    const x = (d ?? {}) as unknown as Record<string, unknown>
    const wave = Number(x.wave)
    return {
      key: str(x.key),
      label: str(x.label) || str(x.key),
      role: str(x.role) || str(x.label) || str(x.key),
      wave: Number.isFinite(wave) && wave >= 1 ? Math.floor(wave) : 1,
      ...(x.always ? { always: true } : {}),
      ...(str(x.overrideField) ? { overrideField: str(x.overrideField).replace(/^form\./, '') } : {}),
      questions: (Array.isArray(x.questions) ? x.questions : []).map(normQuestion).filter((q): q is ReviewQuestion => q !== null),
    } satisfies MatrixDepartment
  })
  const rules = (Array.isArray(row.rules) ? row.rules : []).map((r, i) => {
    const x = (r ?? {}) as unknown as Record<string, unknown>
    return {
      id: str(x.id) || `rule_${i + 1}`,
      ...(str(x.label) ? { label: str(x.label) } : {}),
      match: x.match === 'any' ? 'any' : 'all',
      conditions: (Array.isArray(x.conditions) ? x.conditions : []).map((c) => {
        const y = (c ?? {}) as Record<string, unknown>
        const op = (['eq', 'neq', 'in', 'contains', 'not_empty'] as const).includes(y.op as never) ? y.op as MatrixConditionOp : 'eq'
        const value = Array.isArray(y.value) ? y.value.map(str).filter(Boolean) : str(y.value)
        return { field: str(y.field).replace(/^form\./, ''), op, value }
      }).filter((c) => c.field),
      departments: (Array.isArray(x.departments) ? x.departments : []).map(str).filter(Boolean),
    } satisfies MatrixRule
  })
  const assignments = (Array.isArray(row.assignments) ? row.assignments : []).map((a) => {
    const x = (a ?? {}) as unknown as Record<string, unknown>
    return { role: str(x.role), site: str(x.site), assignee: str(x.assignee) }
  }).filter((a) => a.role && a.assignee)
  return {
    id: str(row.id),
    key: str(row.key),
    name: str(row.name) || 'Untitled matrix',
    description: str(row.description) || null,
    active: row.active !== false,
    siteField: str(row.siteField).replace(/^form\./, '') || DEFAULT_SITE_FIELD,
    departments,
    rules,
    assignments,
    createdAt: row.createdAt as string | undefined,
    updatedAt: row.updatedAt as string | undefined,
    updatedBy: (row.updatedBy as string | null | undefined) ?? null,
  }
}

/** Problems that block saving. Empty = valid. Blank sections are valid: a
 *  matrix with no rules yet is dormant (its always-on departments still review). */
export function validateMatrix(m: RoutingMatrix): string[] {
  const errors: string[] = []
  if (!m.name.trim()) errors.push('Give the matrix a name.')
  if (!KEY_RE.test(m.key) || m.key.length > 64) errors.push('The key must be lowercase letters and digits, joined by - or _.')
  const keys = new Set<string>()
  for (const d of m.departments) {
    if (!KEY_RE.test(d.key)) errors.push(`Department "${d.label || d.key}" needs a key of lowercase letters and digits.`)
    if (keys.has(d.key)) errors.push(`The department key "${d.key}" is used twice.`)
    keys.add(d.key)
    if (!d.role) errors.push(`Department "${d.label}" needs a functional role.`)
    const qids = new Set<string>()
    for (const q of d.questions ?? []) {
      if (qids.has(q.id)) errors.push(`Department "${d.label}" has two questions with the id "${q.id}".`)
      qids.add(q.id)
      if (q.type === 'select' && !(q.options?.length)) errors.push(`Question "${q.label}" (${d.label}) is a choice question with no choices.`)
    }
  }
  for (const r of m.rules) {
    if (r.departments.length === 0) errors.push(`Rule "${r.label || r.id}" names no department.`)
    for (const k of r.departments) if (!keys.has(k)) errors.push(`Rule "${r.label || r.id}" names the unknown department "${k}".`)
    if (r.conditions.length === 0) errors.push(`Rule "${r.label || r.id}" has no condition. Mark the department "Reviews all requests" instead.`)
  }
  const roles = new Set(m.departments.map((d) => d.role.toLowerCase()))
  const seen = new Set<string>()
  for (const a of m.assignments) {
    if (!roles.has(a.role.toLowerCase())) errors.push(`An assignment names the role "${a.role}", which no department uses.`)
    const k = `${a.role.toLowerCase()}|${a.site.toLowerCase()}`
    if (seen.has(k)) errors.push(`The role "${a.role}" has two assignees for ${a.site ? `site ${a.site}` : 'the default'}.`)
    seen.add(k)
  }
  return errors
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

function formBag(dims: Record<string, unknown>): Record<string, unknown> {
  return (dims.form && typeof dims.form === 'object') ? dims.form as Record<string, unknown> : {}
}

function valuesOf(v: unknown): string[] {
  const list = Array.isArray(v) ? v : [v]
  return list.filter((x) => x !== undefined && x !== null && typeof x !== 'object').map((x) => String(x).trim()).filter(Boolean)
}

/** Does one condition hold for the form values? Multi-values (multi-select,
 *  bulk rows) hold when ANY value matches; neq holds when NONE matches. */
export function conditionHolds(c: MatrixCondition, form: Record<string, unknown>): boolean {
  const have = valuesOf(form[c.field]).map((v) => v.toLowerCase())
  const want = (Array.isArray(c.value) ? c.value : [c.value]).map((v) => String(v).trim().toLowerCase()).filter(Boolean)
  switch (c.op) {
    case 'not_empty': return have.length > 0
    case 'eq': return have.some((v) => want.includes(v))
    case 'in': return have.some((v) => want.includes(v))
    case 'neq': return !have.some((v) => want.includes(v))
    case 'contains': return have.some((v) => want.some((w) => v.includes(w)))
    default: return false
  }
}

export function ruleHolds(r: MatrixRule, form: Record<string, unknown>): boolean {
  if (r.conditions.length === 0) return false
  return r.match === 'any' ? r.conditions.some((c) => conditionHolds(c, form)) : r.conditions.every((c) => conditionHolds(c, form))
}

/** The department keys a request selects, in matrix order. */
export function selectedDepartments(m: RoutingMatrix, dims: Record<string, unknown>): string[] {
  const form = formBag(dims)
  const picked = new Set<string>()
  for (const d of m.departments) if (d.always) picked.add(d.key)
  for (const r of m.rules) if (ruleHolds(r, form)) for (const k of r.departments) picked.add(k)
  return m.departments.filter((d) => picked.has(d.key)).map((d) => d.key)
}

/** The site value of a request (the matrix's site field). */
export function requestSite(m: Pick<RoutingMatrix, 'siteField'>, dims: Record<string, unknown>): string | null {
  const v = valuesOf(formBag(dims)[m.siteField || DEFAULT_SITE_FIELD])[0]
  return v ?? null
}

/** Who reviews for a department: the override field, then the site row, then the default. */
export function departmentAssignee(m: RoutingMatrix, d: MatrixDepartment, dims: Record<string, unknown>): { assignee: string; source: ReviewSeat['source'] } | null {
  if (d.overrideField) {
    const v = valuesOf(formBag(dims)[d.overrideField])
    if (v.length > 0) return { assignee: v.join(', '), source: 'override' }
  }
  const site = (requestSite(m, dims) ?? '').toLowerCase()
  const role = d.role.toLowerCase()
  const bySite = site ? m.assignments.find((a) => a.role.toLowerCase() === role && a.site.toLowerCase() === site) : undefined
  if (bySite) return { assignee: bySite.assignee, source: 'site' }
  const def = m.assignments.find((a) => a.role.toLowerCase() === role && !a.site)
  return def ? { assignee: def.assignee, source: 'default' } : null
}

export interface MatrixExpansion {
  seats: ReviewSeat[]
  waves: PlanWave[]
  site: string | null
  /** Departments with no reviewer (blocks the step: fail closed). */
  problems: string[]
}

/**
 * Expand a matrix for one request into review seats and waves.
 * `only` limits it to named departments (a Department review step), which
 * skips the rules. Group tokens expand to their members; a group with no
 * members, or an assignee that resolves to nothing, is a problem.
 */
export function expandMatrix(
  m: RoutingMatrix,
  dims: Record<string, unknown>,
  groups: PeopleGroup[],
  opts: { only?: string[]; join?: WaveJoinRule } = {},
): MatrixExpansion {
  const site = requestSite(m, dims)
  const keys = opts.only && opts.only.length > 0
    ? m.departments.filter((d) => opts.only!.includes(d.key)).map((d) => d.key)
    : selectedDepartments(m, dims)
  const problems: string[] = []
  if (opts.only) for (const k of opts.only) if (!m.departments.some((d) => d.key === k)) problems.push(`The department "${k}" is not in the routing matrix "${m.name}".`)
  const seats: ReviewSeat[] = []
  for (const key of keys) {
    const d = m.departments.find((x) => x.key === key)!
    const who = departmentAssignee(m, d, dims)
    if (!who) {
      problems.push(`No reviewer is set for ${d.label} (role ${d.role})${site ? ` at site ${site}` : ''}. Add one on Setup > Routing Matrix.`)
      continue
    }
    const tokens = who.assignee.split(',').map((t) => t.trim()).filter(Boolean)
    const members = expandTokens(tokens, groups, dims).filter((x) => !/^@/.test(x))
    const emptyGroup = tokens.some((t) => /^@/.test(t)) && members.length === 0
    if (members.length === 0 || emptyGroup) {
      problems.push(`The reviewer for ${d.label} (${who.assignee}) has no members.`)
      continue
    }
    seats.push({
      department: d.key, departmentLabel: d.label, role: d.role, wave: d.wave,
      members, source: who.source, assignee: who.assignee,
      ...(d.questions && d.questions.length > 0 ? { questions: d.questions } : {}),
    })
  }
  // Waves in order of their number; empty wave numbers collapse.
  const waveNos = [...new Set(seats.map((s) => s.wave))].sort((a, b) => a - b)
  const renumber = new Map(waveNos.map((n, i) => [n, i + 1]))
  for (const s of seats) s.wave = renumber.get(s.wave) ?? 1
  const waves: PlanWave[] = waveNos.map((_, i) => {
    const inWave = seats.filter((s) => s.wave === i + 1)
    return {
      label: `Wave ${i + 1}: ${inWave.map((s) => s.departmentLabel).join(', ')}`,
      approvers: [...new Set(inWave.flatMap((s) => s.members))],
      departments: inWave.map((s) => s.department),
      join: opts.join ?? 'all',
    }
  })
  return { seats, waves, site, problems }
}

/** Questions a reviewer must answer for their departments, and which are missing. */
export function missingAnswers(seats: ReviewSeat[], departments: string[], answers: Record<string, Record<string, unknown>> | undefined): string[] {
  const out: string[] = []
  for (const seat of seats.filter((s) => departments.includes(s.department))) {
    for (const q of seat.questions ?? []) {
      if (!q.required) continue
      const v = answers?.[seat.department]?.[q.id]
      if (v === undefined || v === null || String(v).trim() === '') out.push(`${seat.departmentLabel}: ${q.label}`)
    }
  }
  return out
}

/** Answers as readable text, for the request history and CSV export. */
export function answersText(seats: ReviewSeat[] | undefined, answers: Record<string, Record<string, unknown>> | undefined): string {
  if (!answers) return ''
  const parts: string[] = []
  for (const [dept, byQ] of Object.entries(answers)) {
    const seat = seats?.find((s) => s.department === dept)
    for (const [qid, v] of Object.entries(byQ ?? {})) {
      if (v === undefined || v === null || String(v).trim() === '') continue
      const q = seat?.questions?.find((x) => x.id === qid)
      const shown = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)
      parts.push(`${seat?.departmentLabel ?? dept} - ${q?.label ?? qid}: ${shown}`)
    }
  }
  return parts.join('; ')
}

/** Every department review answer on a run's plan, as one line of text (for
 *  the CSV export). Answers from earlier loop rounds are left out. */
export function planReviewAnswersText(plan: { steps: Array<{ name: string; round?: number; reviewSeats?: ReviewSeat[]; decisions?: Record<string, { answers?: Record<string, Record<string, unknown>>; round?: number }> }> } | null | undefined): string {
  if (!plan) return ''
  const parts: string[] = []
  for (const step of plan.steps) {
    if (!step.reviewSeats || step.reviewSeats.length === 0) continue
    for (const d of Object.values(step.decisions ?? {})) {
      if (!d.answers || (d.round ?? 0) !== (step.round ?? 0)) continue
      const text = answersText(step.reviewSeats, d.answers)
      if (text) parts.push(text)
    }
  }
  return parts.join('; ')
}
